import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import InfiniteScroll from "react-infinite-scroll-component";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Download, Inbox, X } from "@thuthi/ui/icons";
import { Badge } from "@thuthi/ui/components/badge";
import { Button } from "@thuthi/ui/components/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@thuthi/ui/components/empty";
import { Item, ItemActions, ItemContent, ItemGroup, ItemSeparator, ItemTitle } from "@thuthi/ui/components/item";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@thuthi/ui/components/dialog";
import { Separator } from "@thuthi/ui/components/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@thuthi/ui/components/tooltip";
import { LoadingOverlay } from "@thuthi/ui/components/loading-overlay";
import { Spinner } from "@thuthi/ui/components/spinner";
import { ToggleGroup, ToggleGroupItem } from "@thuthi/ui/components/toggle-group";
import {
  formatBytes,
  formatShortDateTime,
  openUrl,
  shortUrl,
  topicDisplayName,
  topicUrl,
} from "../app/utils";
import { formatMessage, formatTitle, isImage, notificationAttachments } from "../app/notificationUtils";
import subscriptionManager from "../app/SubscriptionManager";
import notifier from "../app/Notifier";
import nativeBridge from "../app/NativeBridge";
import logoOutline from "../img/ntfy-outline.svg";
import AttachmentIcon from "./AttachmentIcon";
import TopicAvatar from "./TopicAvatar";
import { VerticallyCenteredContainer } from "./styles";
import { useAutoSubscribe } from "./hooks";
import type { Attachment as NotificationAttachment, Notification, NotificationAction, Subscription } from "../app/types";

// React 19 타입에서 제거된 <tt> 요소를 이 파일에서만 다시 선언한다 (런타임 영향 없음).
declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      tt: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

/** Outlet context provided by App.tsx (<Outlet context={{ subscriptions, selected }} />). */
interface NotificationsOutletContext {
  subscriptions?: Subscription[];
  selected?: Subscription;
}

const ExtLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a {...props} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline" />
);

export const AllSubscriptions = () => {
  const { subscriptions } = useOutletContext<NotificationsOutletContext>();
  if (!subscriptions) {
    return <Loading />;
  }
  return <AllSubscriptionsList subscriptions={subscriptions} />;
};

export const SingleSubscription = () => {
  const { subscriptions, selected } = useOutletContext<NotificationsOutletContext>();
  useAutoSubscribe(subscriptions, selected);
  if (!selected) {
    return <Loading />;
  }
  return <SingleSubscriptionList subscription={selected} />;
};

interface AllSubscriptionsListProps {
  subscriptions: Subscription[];
}

const AllSubscriptionsList = (props: AllSubscriptionsListProps) => {
  const { subscriptions } = props;
  const notifications = useLiveQuery(() => subscriptionManager.getAllNotifications(), []);
  if (notifications === null || notifications === undefined) {
    return <Loading />;
  }
  if (subscriptions.length === 0) {
    return <NoSubscriptions />;
  }
  if (notifications.length === 0) {
    return <NoNotificationsWithoutSubscription subscriptions={subscriptions} />;
  }
  return (
    <NotificationWorkspace
      subscriptions={subscriptions}
      notifications={notifications}
      renderList={(list) => <NotificationList key="all" notifications={list} messageBar={false} />}
    />
  );
};

interface SingleSubscriptionListProps {
  subscription: Subscription;
}

const SingleSubscriptionList = (props: SingleSubscriptionListProps) => {
  const { subscription } = props;
  const notifications = useLiveQuery(() => subscriptionManager.getNotifications(subscription.id), [subscription]);
  const deepLinked = useDeepLinkedNotification(notifications);
  if (notifications === null || notifications === undefined) {
    return <Loading />;
  }
  return (
    <>
      {notifications.length === 0 ? (
        <NoNotifications subscription={subscription} />
      ) : (
        <NotificationWorkspace
          subscription={subscription}
          notifications={notifications}
          renderList={(list) => <NotificationList id={subscription.id} notifications={list} messageBar />}
        />
      )}
      {deepLinked.notification && <NotificationDetailModal open notification={deepLinked.notification} onClose={deepLinked.close} />}
    </>
  );
};

const DEEP_LINK_WAIT_MS = 10_000;

/**
 * 알림 딥링크 `/<topic>?message=<메시지 id>` — 앱 알림을 누르면 이 주소로 열린다(apps/app NotificationService).
 * 그 알림이 로컬 DB 에 들어오면(폴링·SSE 로 조금 늦게 올 수 있다) 상세 팝업을 열고 읽음 처리한 뒤,
 * 주소에서 message 를 지운다(replace — 뒤로가기·새로고침이 다시 열지 않게). 10초 안에 안 오면 그냥 지운다.
 * 같은 seq 로 덮어쓴 메시지도 찾도록 sequence_id 로도 맞춘다.
 */
const useDeepLinkedNotification = (notifications: Notification[] | undefined) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const wanted = searchParams.get("message");
  const [openId, setOpenId] = useState<string | null>(null);

  const stripParam = useCallback(() => {
    const next = new URLSearchParams(location.search);
    next.delete("message");
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!wanted || !notifications) {
      return;
    }
    const match = notifications.find((n) => n.id === wanted) ?? notifications.find((n) => (n.sequence_id || n.sequenceId) === wanted);
    if (!match) {
      return;
    }
    setOpenId(match.id);
    if (match.new === 1) {
      subscriptionManager.markNotificationRead(match.id);
    }
    stripParam();
  }, [wanted, notifications, stripParam]);

  useEffect(() => {
    if (!wanted) {
      return undefined;
    }
    const timer = setTimeout(() => {
      console.log(`[Notifications] Deep-linked notification ${wanted} did not arrive, giving up`);
      stripParam();
    }, DEEP_LINK_WAIT_MS);
    return () => clearTimeout(timer);
  }, [wanted, stripParam]);

  return {
    notification: openId ? notifications?.find((n) => n.id === openId) : undefined,
    close: () => setOpenId(null),
  };
};

type NotificationFilter = "all" | "unread" | "actionable";

interface NotificationWorkspaceProps {
  subscriptions?: Subscription[];
  subscription?: Subscription;
  notifications: Notification[];
  renderList: (notifications: Notification[]) => React.ReactNode;
}

// 시안 A-2: 제목·부제 줄 + [전체|안 읽음|조치] 필터 세그먼트 + 한 장으로 묶인 목록.
// (예전 요약 칸 Total/Unread/Actionable 은 숫자만 보여줬다 — 이제 같은 숫자로 목록을 실제로 거른다.)
const NotificationWorkspace = ({ subscriptions, subscription, notifications, renderList }: NotificationWorkspaceProps) => {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<NotificationFilter>("all");
  useEffect(() => setFilter("all"), [subscription?.id]);
  const isUnread = (notification: Notification) => notification.new === 1;
  const isActionable = (notification: Notification) => viewActions(notification).length > 0;
  const unread = notifications.filter(isUnread).length;
  const actions = notifications.filter(isActionable).length;
  const latest = notifications[0] ? formatShortDateTime(notifications[0].time, i18n.language) : "";
  const title = subscription ? topicDisplayName(subscription) : "알림";
  const subtitle = [
    subscription ? shortUrl(topicUrl(subscription.baseUrl, subscription.topic)) : `구독 ${subscriptions?.length ?? 0}개`,
    latest && `최근 ${latest}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const visible =
    filter === "unread" ? notifications.filter(isUnread) : filter === "actionable" ? notifications.filter(isActionable) : notifications;

  const handleMarkAllRead = async () => {
    if (subscription) {
      await subscriptionManager.markNotificationsRead(subscription.id);
      return;
    }
    const unreadSubscriptions = (subscriptions || []).filter((item) => (item.new ?? 0) > 0);
    await Promise.all(unreadSubscriptions.map((item) => subscriptionManager.markNotificationsRead(item.id)));
  };

  return (
    <div className={`mx-auto mt-3 flex w-full max-w-3xl flex-col gap-3 px-3 sm:px-4 md:mt-5 ${subscription ? "mb-28" : "mb-6"}`}>
      <div className="flex items-end justify-between gap-3 px-1">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-0.5 truncate text-[13px]">{subtitle}</p>
        </div>
        {unread > 0 && (
          <Button variant="soft" size="sm" onClick={handleMarkAllRead} className="shrink-0">
            <Check />
            {t("notifications_mark_all_read")}
          </Button>
        )}
      </div>
      <ToggleGroup
        type="single"
        className="w-full"
        value={filter}
        onValueChange={(value) => value && setFilter(value as NotificationFilter)}
        aria-label="알림 거르기"
      >
        <ToggleGroupItem value="all">
          전체 <span className="tabular-nums opacity-60">{notifications.length}</span>
        </ToggleGroupItem>
        <ToggleGroupItem value="unread">
          안 읽음 <span className="tabular-nums opacity-60">{unread}</span>
        </ToggleGroupItem>
        <ToggleGroupItem value="actionable">
          조치 <span className="tabular-nums opacity-60">{actions}</span>
        </ToggleGroupItem>
      </ToggleGroup>
      {visible.length > 0 ? (
        renderList(visible)
      ) : (
        <Empty className="bg-card shadow-card py-10">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Inbox />
            </EmptyMedia>
            <EmptyTitle className="text-base">{filter === "unread" ? "안 읽은 알림이 없어요" : "조치할 알림이 없어요"}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
};

interface NotificationListProps {
  id?: string;
  notifications: Notification[];
  messageBar: boolean;
}

const NotificationList = (props: NotificationListProps) => {
  const { t } = useTranslation();
  const pageSize = 20;
  const { notifications } = props;
  const [maxCount, setMaxCount] = useState(pageSize);
  const count = Math.min(notifications.length, maxCount);

  useEffect(
    () => () => {
      setMaxCount(pageSize);
      const main = document.getElementById("main");
      if (main) {
        main.scrollTo(0, 0);
      }
    },
    [props.id]
  );

  return (
    <InfiniteScroll
      dataLength={count}
      next={() => setMaxCount((prev) => prev + pageSize)}
      hasMore={count < notifications.length}
      loader={
        <div className="flex justify-center py-3">
          <Spinner className="text-muted-foreground" />
        </div>
      }
      scrollThreshold={0.7}
      scrollableTarget="main"
    >
      <ItemGroup variant="card" aria-label={t("notifications_list")}>
        {notifications.slice(0, count).map((notification, index) => (
          <React.Fragment key={notification.id}>
            {index > 0 && <ItemSeparator />}
            <NotificationItem notification={notification} />
          </React.Fragment>
        ))}
      </ItemGroup>
    </InfiniteScroll>
  );
};

/**
 * Replace links with <Link/> components; this is a combination of the genius function
 * in [1] and the regex in [2].
 *
 * [1] https://github.com/facebook/react/issues/3386#issuecomment-78605760
 * [2] https://github.com/bryanwoods/autolink-js/blob/master/autolink.js#L9
 */
const autolink = (s: string) => {
  const parts: React.ReactNode[] = s.split(/(\bhttps?:\/\/[-A-Z0-9+\u0026\u2019@#/%?=()~_|!:,.;]*[-A-Z0-9+\u0026@#/%=~()_|]\b)/gi);
  for (let i = 1; i < parts.length; i += 2) {
    const url = parts[i] as string;
    parts[i] = (
      <a key={i} href={url} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
        {shortUrl(url)}
      </a>
    );
  }
  return <>{parts}</>;
};

/** 본문은 글자 그대로 + URL 링크(마크다운은 v2 에 없다, 2026-10-07) */
const NotificationBody = ({ notification }: { notification: Notification }) => autolink(formatMessage(notification));

interface NotificationItemProps {
  notification: Notification;
}

const NotificationItem = (props: NotificationItemProps) => {
  const { t, i18n } = useTranslation();
  const { notification } = props;
  const attachments = notificationAttachments(notification);
  const images = attachments.filter((a) => isImage(a) && !isExpired(a));
  const [detailOpen, setDetailOpen] = useState(false);
  const date = formatShortDateTime(notification.time, i18n.language);
  const notificationTags = notification.tags ?? [];
  const topicName = notification.topic || "notification";
  // "모든 알림" 목록에서도 그 알림의 구독 아이콘을 쓴다(App 의 Outlet context)
  const outlet = useOutletContext<NotificationsOutletContext | undefined>();
  const subscriptionIcon = outlet?.subscriptions?.find((s) => s.id === notification.subscriptionId)?.icon;
  const title = notification.title ? formatTitle(notification) : formatMessage(notification);
  const handleOpen = async () => {
    setDetailOpen(true);
    if (notification.new === 1) {
      console.log(`[Notifications] Marking notification ${notification.id} as read after opening details`);
      await subscriptionManager.markNotificationRead(notification.id);
    }
  };
  const handleKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      handleOpen();
    }
  };
  const handleDelete = async () => {
    console.log(`[Notifications] Deleting notification ${notification.id}`);
    await subscriptionManager.deleteNotification(notification.id);
  };
  const handleMarkRead = async () => {
    console.log(`[Notifications] Marking notification ${notification.id} as read`);
    await subscriptionManager.markNotificationRead(notification.id);
  };

  const unread = notification.new === 1;
  // 목록에선 짧게(오늘이면 시각, 아니면 월·일) — 전체 날짜는 title 로
  const when = new Date(notification.time * 1000);
  const shortWhen =
    when.toDateString() === new Date().toDateString()
      ? when.toLocaleTimeString(i18n.language, { hour: "numeric", minute: "2-digit" })
      : when.toLocaleDateString(i18n.language, { month: "numeric", day: "numeric" });

  return (
    <>
      <Item
        role="listitem"
        aria-label={t("notifications_list_item")}
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        size="sm"
        className="hover:bg-accent/60 cursor-pointer flex-nowrap items-start gap-3 py-3 pl-5"
      >
        {unread && <span aria-hidden className="bg-primary absolute top-1/2 left-2 size-1.5 -translate-y-1/2 rounded-full" />}
        <TopicAvatar topic={topicName} icon={subscriptionIcon} unread={unread} />
        <ItemContent>
          <ItemTitle className={`w-full ${unread ? "" : "font-medium"}`}>
            <span role="rowheader" className="min-w-0 truncate">
              {title}
            </span>
          </ItemTitle>
          <div className="text-muted-foreground flex flex-row flex-wrap items-center gap-1.5 text-xs">
            <Badge variant="secondary" className="max-w-[150px] px-2 py-0 sm:max-w-[240px]">
              <span className="truncate">{topicName}</span>
            </Badge>
            {images.length > 0 && <span>사진 {images.length}장</span>}
            {attachments.length > images.length && <span>파일 {attachments.length - images.length}개</span>}
            {notificationTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          {images.length > 0 && (
            <div className="mt-1.5 flex gap-1.5" aria-hidden>
              {images.slice(0, 4).map((a, i) => (
                <div key={a.id ?? a.url ?? i} className="bg-muted relative size-14 shrink-0 overflow-hidden rounded-md border">
                  <img src={a.thumb || a.url} alt="" loading="lazy" className="size-full object-cover" />
                  {i === 3 && images.length > 4 && (
                    <span className="absolute inset-0 grid place-items-center bg-black/50 text-sm font-semibold text-white">+{images.length - 4}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </ItemContent>
        <div className="flex shrink-0 flex-col items-end gap-1" onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()}>
          <span className="text-muted-foreground text-[11px] whitespace-nowrap tabular-nums" title={date}>
            {shortWhen}
          </span>
          <ItemActions className="gap-0.5">
            {unread && (
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="size-7" onClick={handleMarkRead} aria-label={t("notifications_mark_read")}>
                    <Check />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("notifications_mark_read")}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="size-7" onClick={handleDelete} aria-label={t("notifications_delete")}>
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("notifications_delete")}</TooltipContent>
            </Tooltip>
          </ItemActions>
        </div>
      </Item>
      <NotificationDetailModal
        open={detailOpen}
        notification={notification}
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
};

interface NotificationDetailModalProps {
  open: boolean;
  notification: Notification;
  onClose: () => void;
}

const NotificationDetailModal = ({ open, notification, onClose }: NotificationDetailModalProps) => {
  const { t, i18n } = useTranslation();
  const attachments = notificationAttachments(notification);
  const date = formatShortDateTime(notification.time, i18n.language);
  const notificationTags = notification.tags ?? [];
  const detailTitle = notification.title ? formatTitle(notification) : notification.topic || "Notification";
  const showActions = viewActions(notification).length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-24px)] w-[min(760px,calc(100vw-24px))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
      >
        <div className="min-h-0 flex-auto overflow-y-auto overscroll-contain p-4 sm:p-5">
          <div className="mb-4 flex items-start gap-3">
            <div className="min-w-0 grow">
              <div className="mb-2 flex flex-row flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="bg-background h-[22px] rounded-lg font-bold">
                  {notification.topic || "notification"}
                </Badge>
                <span className="text-muted-foreground text-[13px] font-semibold">{date}</span>
                {notification.new === 1 && (
                  <Badge variant="secondary" className="bg-primary/10 text-primary h-[22px] rounded-lg font-bold">
                    {t("notifications_new_indicator")}
                  </Badge>
                )}
              </div>
              <DialogTitle className="text-[1.25rem] leading-[1.18] font-semibold [overflow-wrap:anywhere] sm:text-[1.55rem]">
                {detailTitle}
              </DialogTitle>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="shrink-0" aria-label={t("common_cancel")}>
                <X />
              </Button>
            </DialogClose>
          </div>
          <div className="text-foreground overflow-x-auto border-t pt-4 leading-[1.7] whitespace-pre-line [overflow-wrap:anywhere]">
            <NotificationBody notification={notification} />
          </div>
          {attachments.length > 0 && <Attachments attachments={attachments} />}
          {notificationTags.length > 0 && (
            <div className="mt-3 flex flex-row flex-wrap gap-1.5" aria-label={t("notifications_tags")}>
              {notificationTags.map((tag) => (
                <Badge key={tag} variant="secondary" className="bg-accent text-muted-foreground rounded-lg font-semibold">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {showActions && (
          <>
            <Separator />
            <NotificationActions notification={notification} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface NotificationActionsProps {
  notification: Notification;
}

const NotificationActions = ({ notification }: NotificationActionsProps) => (
  <div className="bg-card flex shrink-0 flex-row flex-wrap items-center gap-1.5 px-4 py-2.5 sm:px-5">
    <UserActions notification={notification} />
  </div>
);

const isExpired = (a: NotificationAttachment) => !!a.expires && a.expires < Date.now() / 1000;

/** 서버에 있는 파일이면 ?download=1 로 내려받게, URL 첨부면 그 주소 그대로 */
const downloadUrl = (a: NotificationAttachment) => (a.id && a.url ? `${a.url}${a.url.includes("?") ? "&" : "?"}download=1` : a.url);

/**
 * 첨부 여러 개 — 이미지는 썸네일 격자(한 장이면 크게, width/height 로 칸 비율을 먼저 잡아 레이아웃이 뛰지 않게),
 * 누르면 전체 화면 보기(좌우 넘기기·확대·내려받기). 나머지 파일은 칩, URL 첨부는 링크.
 */
const Attachments = ({ attachments }: { attachments: NotificationAttachment[] }) => {
  const images = attachments.filter((a) => isImage(a) && !isExpired(a));
  const others = attachments.filter((a) => !images.includes(a));
  const [viewer, setViewer] = useState<number | null>(null);
  const single = images.length === 1 ? images[0] : null;
  let gridCols = "grid-cols-2";
  if (images.length >= 5 || images.length === 3) {
    gridCols = "grid-cols-3";
  }
  return (
    <>
      {single && (
        <button
          type="button"
          onClick={() => setViewer(0)}
          className="bg-muted mt-4 block w-full cursor-zoom-in overflow-hidden rounded-lg border"
          style={single.width && single.height ? { aspectRatio: `${single.width} / ${single.height}`, maxHeight: 420 } : { maxHeight: 420 }}
        >
          <img src={single.thumb || single.url} alt={single.name} loading="lazy" className="size-full max-h-[420px] object-contain" />
        </button>
      )}
      {images.length > 1 && (
        <div className={`mt-4 grid gap-1.5 ${gridCols}`}>
          {images.map((a, i) => (
            <button
              key={a.id ?? a.url ?? i}
              type="button"
              onClick={() => setViewer(i)}
              className="bg-muted aspect-square cursor-zoom-in overflow-hidden rounded-lg border"
              aria-label={a.name}
            >
              <img src={a.thumb || a.url} alt={a.name} loading="lazy" className="size-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {others.map((a, i) => (
        <AttachmentChip key={a.id ?? a.url ?? i} attachment={a} />
      ))}
      <ImageViewer images={images} index={viewer} onIndexChange={setViewer} />
    </>
  );
};

const AttachmentChip = ({ attachment }: { attachment: NotificationAttachment }) => {
  const { t, i18n } = useTranslation();
  const expired = isExpired(attachment);
  const infos: string[] = [];
  if (attachment.size) {
    infos.push(formatBytes(attachment.size));
  }
  if (attachment.expires && !expired) {
    infos.push(t("notifications_attachment_link_expires", { date: formatShortDateTime(attachment.expires, i18n.language) }));
  }
  if (expired) {
    infos.push(t("notifications_attachment_link_expired"));
  }
  const body = (
    <>
      <AttachmentIcon type={attachment.type} />
      <p className="text-foreground ml-2 min-w-0 text-left text-sm [overflow-wrap:anywhere]">
        <b>{attachment.name || attachment.url}</b>
        {infos.length > 0 && (
          <>
            <br />
            {infos.join(", ")}
          </>
        )}
      </p>
    </>
  );
  if (expired || !attachment.url) {
    return <div className="bg-accent/50 mt-3 flex items-center rounded-lg border p-2.5">{body}</div>;
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-accent/50 hover:border-primary mt-3 flex items-center rounded-lg border p-2.5 no-underline"
    >
      {body}
    </a>
  );
};

interface ImageViewerProps {
  images: NotificationAttachment[];
  index: number | null;
  onIndexChange: (index: number | null) => void;
}

/** 전체 화면 사진 보기 — ←/→·버튼·옆으로 밀기로 넘기고, 두 손가락·휠로 확대, 더블클릭으로 원래대로 */
const ImageViewer = ({ images, index, onIndexChange }: ImageViewerProps) => {
  const { t } = useTranslation();
  const open = index !== null && index < images.length;
  const current = open ? images[index] : null;
  const count = images.length;
  const go = useCallback(
    (delta: number) => {
      if (index === null || count < 2) {
        return;
      }
      onIndexChange((index + delta + count) % count);
    },
    [index, count, onIndexChange]
  );
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "ArrowLeft") {
        go(-1);
      } else if (ev.key === "ArrowRight") {
        go(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);
  const navButton = "fixed top-1/2 z-[1] -translate-y-1/2 rounded-full bg-black/35 text-white hover:bg-black/50 hover:text-white";
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onIndexChange(null)}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="top-0 left-0 block h-dvh w-dvw max-w-none translate-x-0 translate-y-0 rounded-none border-0 bg-black/85 p-0 shadow-none sm:max-w-none"
      >
        <DialogTitle className="sr-only">{current?.name || t("notifications_attachment_image")}</DialogTitle>
        {current && (
          <ZoomableImage
            key={index}
            src={current.url}
            alt={current.name || t("notifications_attachment_image")}
            closeLabel={t("common_cancel")}
            onClose={() => onIndexChange(null)}
            onSwipe={(dir) => go(dir === "left" ? 1 : -1)}
          />
        )}
        {count > 1 && (
          <>
            <Button variant="ghost" size="icon" onClick={() => go(-1)} aria-label="이전 사진" className={`${navButton} left-3`}>
              <ChevronLeft />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => go(1)} aria-label="다음 사진" className={`${navButton} right-3`}>
              <ChevronRight />
            </Button>
          </>
        )}
        <div className="fixed bottom-4 left-1/2 z-[1] flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/45 px-3 py-1 text-sm text-white">
          {count > 1 && (
            <span className="tabular-nums">
              {(index ?? 0) + 1} / {count}
            </span>
          )}
          {current?.url && (
            <a
              href={downloadUrl(current)}
              download={current.name}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
              aria-label="내려받기"
            >
              <Download className="size-4" /> 내려받기
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const imageZoomMin = 1;
const imageZoomMax = 6;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** The subset of pointer/mouse event data the gesture math needs. */
interface PointerLike {
  clientX: number;
  clientY: number;
}

interface ImageTransform {
  scale: number;
  x: number;
  y: number;
}

type Gesture =
  | { type: "pinch"; distance: number; center: PointerLike; transform: ImageTransform }
  | { type: "pan"; pointer: PointerLike; transform: ImageTransform };

const pointerDistance = (a: PointerLike, b: PointerLike) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const pointerCenter = (a: PointerLike, b: PointerLike) => ({
  clientX: (a.clientX + b.clientX) / 2,
  clientY: (a.clientY + b.clientY) / 2,
});

interface ZoomableImageProps {
  src?: string;
  alt: string;
  closeLabel: string;
  onClose: () => void;
  /** 확대하지 않은 상태에서 옆으로 밀었을 때(여러 장 넘기기) */
  onSwipe?: (direction: "left" | "right") => void;
}

const ZoomableImage = React.forwardRef<HTMLDivElement, ZoomableImageProps>(({ src, alt, closeLabel, onClose, onSwipe }, forwardedRef) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pointersRef = useRef(new Map<number, React.PointerEvent<HTMLDivElement>>());
  const gestureRef = useRef<Gesture | null>(null);
  const [transform, setTransform] = useState<ImageTransform>({ scale: imageZoomMin, x: 0, y: 0 });

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        const ref = forwardedRef;
        ref.current = node;
      }
    },
    [forwardedRef]
  );

  const constrainTransform = useCallback((next: ImageTransform): ImageTransform => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image || next.scale <= imageZoomMin) {
      return { scale: imageZoomMin, x: 0, y: 0 };
    }
    const maxX = Math.max(0, (image.offsetWidth * next.scale - container.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * next.scale - container.clientHeight) / 2);
    return {
      scale: next.scale,
      x: clamp(next.x, -maxX, maxX),
      y: clamp(next.y, -maxY, maxY),
    };
  }, []);

  const resetTransform = useCallback(() => {
    setTransform({ scale: imageZoomMin, x: 0, y: 0 });
  }, []);

  const startGesture = useCallback(
    (pointers: PointerLike[], currentTransform: ImageTransform) => {
      if (pointers.length >= 2) {
        const center = pointerCenter(pointers[0], pointers[1]);
        gestureRef.current = {
          type: "pinch",
          distance: pointerDistance(pointers[0], pointers[1]),
          center,
          transform: currentTransform,
        };
      } else if (pointers.length === 1) {
        gestureRef.current = {
          type: "pan",
          pointer: pointers[0],
          transform: currentTransform,
        };
      } else {
        gestureRef.current = null;
      }
    },
    []
  );

  const handlePointerDown = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      ev.preventDefault();
      ev.currentTarget.setPointerCapture(ev.pointerId);
      pointersRef.current.set(ev.pointerId, ev);
      startGesture(Array.from(pointersRef.current.values()), transform);
    },
    [startGesture, transform]
  );

  const handlePointerMove = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      if (!pointersRef.current.has(ev.pointerId)) {
        return;
      }
      ev.preventDefault();
      pointersRef.current.set(ev.pointerId, ev);
      const pointers = Array.from(pointersRef.current.values());
      const gesture = gestureRef.current;
      if (!gesture) {
        return;
      }
      if (gesture.type === "pinch" && pointers.length >= 2) {
        const nextCenter = pointerCenter(pointers[0], pointers[1]);
        const nextScale = clamp((gesture.transform.scale * pointerDistance(pointers[0], pointers[1])) / gesture.distance, imageZoomMin, imageZoomMax);
        const scaleRatio = nextScale / gesture.transform.scale;
        const containerRect = containerRef.current!.getBoundingClientRect();
        const centerOffsetX = gesture.center.clientX - containerRect.left - containerRect.width / 2;
        const centerOffsetY = gesture.center.clientY - containerRect.top - containerRect.height / 2;
        const movedX = nextCenter.clientX - gesture.center.clientX;
        const movedY = nextCenter.clientY - gesture.center.clientY;
        setTransform(
          constrainTransform({
            scale: nextScale,
            x: centerOffsetX - (centerOffsetX - gesture.transform.x) * scaleRatio + movedX,
            y: centerOffsetY - (centerOffsetY - gesture.transform.y) * scaleRatio + movedY,
          })
        );
      } else if (gesture.type === "pan" && pointers.length === 1) {
        setTransform(
          constrainTransform({
            scale: gesture.transform.scale,
            x: gesture.transform.x + ev.clientX - gesture.pointer.clientX,
            y: gesture.transform.y + ev.clientY - gesture.pointer.clientY,
          })
        );
      }
    },
    [constrainTransform]
  );

  const handlePointerEnd = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      const gesture = gestureRef.current;
      pointersRef.current.delete(ev.pointerId);
      if (onSwipe && gesture?.type === "pan" && pointersRef.current.size === 0 && transform.scale <= imageZoomMin) {
        const dx = ev.clientX - gesture.pointer.clientX;
        const dy = ev.clientY - gesture.pointer.clientY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          onSwipe(dx < 0 ? "left" : "right");
        }
      }
      startGesture(Array.from(pointersRef.current.values()), transform);
    },
    [startGesture, transform, onSwipe]
  );

  const handleWheel = useCallback(
    (ev: React.WheelEvent<HTMLDivElement>) => {
      ev.preventDefault();
      const containerRect = containerRef.current!.getBoundingClientRect();
      const nextScale = clamp(transform.scale * (ev.deltaY < 0 ? 1.16 : 0.86), imageZoomMin, imageZoomMax);
      const scaleRatio = nextScale / transform.scale;
      const centerOffsetX = ev.clientX - containerRect.left - containerRect.width / 2;
      const centerOffsetY = ev.clientY - containerRect.top - containerRect.height / 2;
      setTransform(
        constrainTransform({
          scale: nextScale,
          x: centerOffsetX - (centerOffsetX - transform.x) * scaleRatio,
          y: centerOffsetY - (centerOffsetY - transform.y) * scaleRatio,
        })
      );
    },
    [constrainTransform, transform]
  );

  return (
    <div
      ref={setContainerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onWheel={handleWheel}
      onDoubleClick={resetTransform}
      className="fixed inset-0 grid touch-none place-items-center overflow-hidden select-none"
      style={{ cursor: transform.scale > imageZoomMin ? "grab" : "zoom-in" }}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        onPointerDown={(ev) => ev.stopPropagation()}
        aria-label={closeLabel}
        className="fixed top-3 right-3 z-[1] rounded-full bg-black/35 text-white hover:bg-black/50 hover:text-white"
      >
        <X />
      </Button>
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        draggable={false}
        className="pointer-events-none max-h-[calc(100dvh-32px)] max-w-[calc(100vw-32px)] object-contain will-change-transform"
        style={{
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
          transformOrigin: "center center",
          transition: pointersRef.current.size > 0 ? "none" : "transform 120ms ease",
        }}
      />
    </div>
  );
});

ZoomableImage.displayName = "ZoomableImage";

interface UserActionsProps {
  notification: Notification;
}

/** 링크 버튼(view)만 — http·broadcast·copy 버튼은 v2 에 없다(2026-10-07). 옛 행에 남아 있어도 그리지 않는다. */
const viewActions = (notification: Notification) => (notification.actions ?? []).filter((a) => a.action === "view" && a.url);

const UserActions = (props: UserActionsProps) => (
  <>
    {viewActions(props.notification).map((action) => (
      <UserAction key={action.id} notification={props.notification} action={action} />
    ))}
  </>
);

const clearNotification = async (notification: Notification) => {
  console.log(`[Notifications] Clearing notification ${notification.id}`);
  const subscription = await subscriptionManager.get(notification.subscriptionId!);
  if (subscription) {
    await notifier.cancel(subscription, notification);
  }
  await subscriptionManager.markNotificationRead(notification.id);
};

interface UserActionProps {
  notification: Notification;
  action: NotificationAction;
}

const UserAction = ({ notification, action }: UserActionProps) => {
  const { t } = useTranslation();
  const handleClick = async () => {
    // 앱 웹뷰에선 네이티브가 바깥 브라우저·앱으로 연다(웹뷰가 그 페이지로 넘어가지 않게)
    if (!(nativeBridge.isNativeApp() && nativeBridge.performUserAction(notification, action))) {
      openUrl(action.url!);
    }
    if (action.clear) {
      await clearNotification(notification);
    }
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" size="sm" onClick={handleClick} aria-label={t("notifications_actions_open_url_title", { url: action.url })}>
          {action.label}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t("notifications_actions_open_url_title", { url: action.url })}</TooltipContent>
    </Tooltip>
  );
};

const NoNotifications = (props: { subscription: Subscription }) => {
  const { t } = useTranslation();
  const topicUrlResolved = topicUrl(props.subscription.baseUrl, props.subscription.topic);
  return (
    <VerticallyCenteredContainer className="max-w-md">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <img src={logoOutline} height="28" width="28" alt={t("action_bar_logo_alt")} />
          </EmptyMedia>
          <EmptyTitle>{t("notifications_none_for_topic_title")}</EmptyTitle>
          <EmptyDescription className="flex flex-col gap-3">
            <p>{t("notifications_none_for_topic_description")}</p>
          <p>
            {t("notifications_example")}:<br />
            <tt>
              {'$ curl -d "Hi" '}
              {topicUrlResolved}
            </tt>
          </p>
            <p>
              <ForMoreDetails />
            </p>
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </VerticallyCenteredContainer>
  );
};

const NoNotificationsWithoutSubscription = (props: { subscriptions: Subscription[] }) => {
  const { t } = useTranslation();
  const subscription = props.subscriptions[0];
  const topicUrlResolved = topicUrl(subscription.baseUrl, subscription.topic);
  return (
    <VerticallyCenteredContainer className="max-w-md">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <img src={logoOutline} height="28" width="28" alt={t("action_bar_logo_alt")} />
          </EmptyMedia>
          <EmptyTitle>{t("notifications_none_for_any_title")}</EmptyTitle>
          <EmptyDescription className="flex flex-col gap-3">
            <p>{t("notifications_none_for_any_description")}</p>
          <p>
            {t("notifications_example")}:<br />
            <tt>
              {'$ curl -d "Hi" '}
              {topicUrlResolved}
            </tt>
          </p>
            <p>
              <ForMoreDetails />
            </p>
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </VerticallyCenteredContainer>
  );
};

const NoSubscriptions = () => {
  const { t } = useTranslation();
  return (
    <VerticallyCenteredContainer className="max-w-md">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <img src={logoOutline} height="28" width="28" alt={t("action_bar_logo_alt")} />
          </EmptyMedia>
          <EmptyTitle>{t("notifications_no_subscriptions_title")}</EmptyTitle>
          <EmptyDescription className="flex flex-col gap-3">
            <p>
              {t("notifications_no_subscriptions_description", {
                linktext: t("nav_button_subscribe"),
              })}
            </p>
            <p>
              <ForMoreDetails />
            </p>
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </VerticallyCenteredContainer>
  );
};

const ForMoreDetails = () => (
  <Trans
    i18nKey="notifications_more_details"
    components={{
      websiteLink: <ExtLink href="https://ntfy.sh" />,
      docsLink: <ExtLink href="https://ntfy.sh/docs" />,
    }}
  />
);

const Loading = () => {
  const { t } = useTranslation();
  return <LoadingOverlay label={t("notifications_loading")} />;
};
